import moment from 'moment';
import { WIDGET_GANTT_CHART_STATUS } from '@utils/enums';
import { GanttTooltip } from './ganttTootip';

const day = 1000 * 60 * 60 * 24;
const maxL1LabelShowedLength = 30;
const RowHeight = 28;

const getTickAdjustInterval = (unit) => {
  const tickInterval = {
    month: 40,
    week: 52,
    day: 80,
  };
  return tickInterval[unit] || 40;
};

const updateXAxisOfDay = (chart, d) => {
  const dayXAxis = chart.get('x-axis-for-day');
  if (dayXAxis) {
    dayXAxis.isDirty = true;
  }
};

const delayUpdateXAxisOfDay = (chart) => {
  setTimeout(() => {
    updateXAxisOfDay(chart);
  }, 600);
};

const getTickMinInterval = (unit) => {
  const tickInterval = {
    month: 35,
    week: 50,
    day: 80,
  };
  return tickInterval[unit] || 35;
};

const adjustTickIntervalByMaxYearAxisGap = (
  yearAxis,
  dayAxis,
  originInterval,
) => {
  const dayAxisUnit = dayAxis?.tickPositions.info?.unitName;
  const yearAxisUnit = yearAxis?.tickPositions.info?.unitName;

  const unitMatched =
    (yearAxisUnit === 'year' && dayAxisUnit === 'month') ||
    (yearAxisUnit === 'month' && dayAxisUnit === 'week') ||
    (yearAxisUnit === 'month' && dayAxisUnit === 'day') ||
    (yearAxisUnit === 'week' && dayAxisUnit === 'day');

  if (unitMatched) {
    // let maxYearTimeInterval = 0;
    let maxYearTimeInterval = [
      yearAxis.tickPositions[0],
      yearAxis.tickPositions[1],
    ];
    for (let i = 1; i < yearAxis.tickPositions.length - 1; i++) {
      // maxYearTimeInterval = Math.max(maxYearTimeInterval, yearXAxis.tickPositions[i] - yearXAxis.tickPositions[i - 1]);
      if (
        yearAxis.tickPositions[i] - yearAxis.tickPositions[i - 1] >
        maxYearTimeInterval[1] - maxYearTimeInterval[0]
      ) {
        maxYearTimeInterval = [
          yearAxis.tickPositions[i - 1],
          yearAxis.tickPositions[i],
        ];
      }
    }
    const maxYearTimeIntervalDiff =
      maxYearTimeInterval[1] - maxYearTimeInterval[0];
    const maxYearIntervalPixel =
      dayAxis.len * (maxYearTimeIntervalDiff / (dayAxis.max - dayAxis.min));
    const originIntervalAmount = maxYearIntervalPixel / originInterval;
    let compactIntervalAmount;
    let loosenIntervalAmount;
    if (dayAxisUnit === 'month') {
      // compactIntervalAmount = new Date(maxYearTimeInterval[1]).getMonth() - new Date(maxYearTimeInterval[0]).getMonth();
      compactIntervalAmount = moment(maxYearTimeInterval[1]).diff(
        moment(maxYearTimeInterval[0]),
        'month',
        true,
      );
      const maxOneMonthTime = 60 * 60 * 24 * 31 * 1000;
      loosenIntervalAmount = maxYearTimeIntervalDiff / maxOneMonthTime;
    } else if (dayAxisUnit === 'week' || dayAxisUnit === 'day') {
      compactIntervalAmount = moment(maxYearTimeInterval[1]).diff(
        moment(maxYearTimeInterval[0]),
        'day',
        true,
      );
      loosenIntervalAmount = compactIntervalAmount;
    }
    // if(originIntervalAmount > compactIntervalAmount) return maxYearIntervalPixel / compactIntervalAmount;
    if (originIntervalAmount > compactIntervalAmount) {
      return (
        maxYearIntervalPixel /
        Math.min(
          originIntervalAmount,
          loosenIntervalAmount,
          compactIntervalAmount,
        )
      );
    }
  }
  return originInterval;
};

const getEndDateArray = (rawTableRowDataArray) => {
  const endDateArray = [];
  rawTableRowDataArray.map((x) => {
    if (x.endDate) {
      endDateArray.push(new Date(x.endDate));
    }
    if (x.type === WIDGET_GANTT_CHART_STATUS.MILESTONE) {
      endDateArray.push(new Date(x.startDate));
    }
    return x;
  });

  return endDateArray;
};

const getStartDateArray = (rawTableRowDataArray) => {
  const startDateArray = [];
  rawTableRowDataArray.map((x) => {
    startDateArray.push(new Date(x.startDate));
    return x;
  });

  return startDateArray;
};

const getMaxDate = (rawTableRowDataArray) =>
  new Date(Math.max(...getEndDateArray(rawTableRowDataArray)));

const getMinDate = (rawTableRowDataArray) =>
  new Date(Math.min(...getStartDateArray(rawTableRowDataArray)));

const getAllSundayArrayFromRange = (start, end) => {
  const result = [];

  const startDate = moment(start);
  const endDate = moment(end);

  const current = startDate.clone();

  while (current.add(1, 'd').isBefore(endDate)) {
    if (current.weekday() === 0) {
      // console.log('XXXcurrent.clone()', moment(current.clone()).format());
      result.push(current.clone().add(1, 'd'));
    }
  }

  if (endDate.weekday() !== 0) {
    result.push(endDate);
  }

  return result;
};

const getSmallTickMapNumberOfTickDay = (sundayArray, activitiesArray) => {
  const result = new Map();

  sundayArray.forEach((sunday, i, arr) => {
    // console.log('XXXsunday', moment(sunday).format());
    if (i === 0) {
      result.set(
        sunday,
        activitiesArray.filter(
          (a) => !moment(a.endDate).isAfter(sunday) && a.type === 'Activity',
        ).length,
      );
    } else {
      result.set(
        sunday,
        activitiesArray.filter(
          (a) =>
            // console.log('XXXa.endDate', moment(a.endDate).format());
            // console.log('XXXa.beforeendDate', moment(a.endDate).format());
            !moment(a.endDate).isAfter(sunday) &&
            moment(a.endDate).isAfter(arr[i - 1]) &&
            a.type === 'Activity',
        ).length,
      );
    }
  });

  return result;
};

// 设置每一项的颜色
const setColor = (item) => {
  switch (item.status) {
    case 'On Track':
      item.color = '#22992E'; // 绿
      break;
    case 'At Risk':
      item.color = '#FFB600'; // 黄·
      break;
    case 'Delayed':
      item.color = '#C52A1A'; // 红
      break;
    case 'Completed':
      item.color = '#0089EB'; // 蓝
      break;
    case 'Not Started':
      item.color = '#666666'; // 灰
      break;
    default:
      item.color = '#666666'; // 灰
  }
};

const getDurationOfAll = (inputData) =>
  getMaxDate(inputData).getTime() - getMinDate(inputData).getTime();

export const buildGanttSeriesData = (inputData, isGanttCollapsed) => {
  const mapOfParent = new Map();
  const clonedInputData = JSON.parse(JSON.stringify(inputData));
  const isNotesHide = inputData.length !== 0 && inputData[0].isnoteshide;

  let currentParentId;
  clonedInputData.forEach((x) => {
    setColor(x);
    if (x.isParent) {
      currentParentId = x.id;
    }

    // if (x.startDate === x.endDate) {
    x.endDate = `${x.endDate} 24: 00`;
    // }

    if (mapOfParent.get(currentParentId)) {
      mapOfParent.set(currentParentId, [
        ...mapOfParent.get(currentParentId),
        x,
      ]);
    } else {
      mapOfParent.set(currentParentId, [x]);
    }
  });

  const currentL1ObjectData = [];

  // for xAxisMin xAxisMax
  const xAxisMin = getMinDate(inputData).getTime();
  const xAxisMax = getMaxDate(inputData).getTime();
  const rangeOfAll = getDurationOfAll(inputData);
  const minChartxAxis =
    xAxisMin - (rangeOfAll < 30 * day ? rangeOfAll * 0.1 : rangeOfAll * 0.05);
  const maxChartxAxis =
    xAxisMax + (rangeOfAll < 30 * day ? rangeOfAll * 0.1 : rangeOfAll * 0.05);

  mapOfParent.forEach(function (v, k) {
    // for small tick
    const activitiesArray = v.slice(1);

    const currentMin = getMinDate(activitiesArray);
    const currentMax = getMaxDate(activitiesArray);

    const currentSundayArray = getAllSundayArrayFromRange(
      currentMin,
      currentMax,
    );

    const smallTickMap = getSmallTickMapNumberOfTickDay(
      currentSundayArray,
      activitiesArray,
    );
    // console.log('XXXcurrentSundayArray', currentSundayArray);
    // console.log('XXXsmallTickMap', smallTickMap);

    smallTickMap.forEach((number, currentTickDay) => {
      if (number !== 0) {
        currentL1ObjectData.push({
          name: v[0].name,
          id: `-L1-tick-${v[0].name}-${currentTickDay}`,
          color: 'transparent',
          start: currentTickDay.valueOf(),
          end: currentTickDay.valueOf(),
          notes: '',
          isTick: true,
          parentNotes: v[0].notes && !isNotesHide ? v[0].notes : '',
          activityNumber: number,
        });
      }
    });

    // for xAxisMin xAxisMax
    currentL1ObjectData.push({
      name: v[0].name,
      id: `-L1-xAxisMinMax-${v[0].name}`,
      color: 'transparent',
      start: minChartxAxis,
      end: maxChartxAxis,
      notes: '',
      isxAxisMinMax: true,
      status: '',
      parentNotes: v[0].notes && !isNotesHide ? v[0].notes : '',
    });

    v.forEach((data, index, array) => {
      if (index === 0) {
        currentL1ObjectData.push({
          name: data.name,
          id: `-L1-${data.name}`,
          color: data.color,
          start: new Date(`${data.startDate} 0: 00`).getTime(),
          end: new Date(data.endDate).getTime(),
          milestone: data.type === WIDGET_GANTT_CHART_STATUS.MILESTONE,
          notes: data.notes && !isNotesHide ? data.notes : '',
          startString: data.startDate,
          endString: data.endDate,
          status: data.status,
        });
      } else {
        const duplicateNumber = array
          .filter((x) => x.name === data.name)
          .findIndex((x) => x.id === data.id);
        currentL1ObjectData.push({
          name: data.name + ' '.repeat(duplicateNumber),
          id: `-L2-${data.name}${index}-Parent-${array[0].name}`,
          parent: `-L1-${array[0].name}`,
          color: data.color,
          start: new Date(`${data.startDate} 0: 00`).getTime(),
          end: new Date(data.endDate).getTime(),
          milestone: data.type === WIDGET_GANTT_CHART_STATUS.MILESTONE,
          notes: data.notes && !isNotesHide ? data.notes : '',
          startString: data.startDate,
          endString: data.endDate,
          status: data.status,
        });
      }
    });
  });

  return currentL1ObjectData;
};

export const buildGanttChartOption = (
  inputData,
  isGanttCollapsed,
  setIsCollapseAllButton,
  chartRef,
  setChartRedraw,
  isLightMode,
) => {
  // console.log('inputData', inputData);

  const series = [];
  const currentL1Object = {
    // name: k,
    borderRadius: 1,
    marker: { lineWidth: 0 },
  };
  currentL1Object.data = buildGanttSeriesData(inputData, isGanttCollapsed);
  series.push(currentL1Object);
  // console.log('series', series);
  const isNotesHide = inputData.length !== 0 && inputData[0].isnoteshide;

  const rightBorderLineHeight = isGanttCollapsed
    ? RowHeight * inputData.filter((d) => d.isParent).length
    : RowHeight * inputData.length;

  const xMaxDate = getMaxDate(inputData).getTime();
  const xMinDate = getMinDate(inputData).getTime();
  let xMinAfterNYear = getMinDate(inputData).setFullYear(
    getMinDate(inputData).getFullYear() + 1,
  );
  //根据 图表X轴最小时间 逐年增加，
  //直到最终超过图标X轴的最大时间，将其设置为X轴最大值。
  //并记录年数设定致date range(最多设定为5年后)
  let nYear = 1;
  while (xMinAfterNYear < xMaxDate && nYear < 5) {
    const dateAfterYear = new Date(xMinAfterNYear);
    xMinAfterNYear = dateAfterYear.setFullYear(dateAfterYear.getFullYear() + 1);
    nYear++;
  }

  const ganttDateFormatter = (ctx) => {
    const formatStr = ctx.dateTimeLabelFormat;
    if (formatStr === '%m/%e/%Y') {
      const date = new Date(ctx.value);
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }

    return ctx.chart.time.dateFormat(formatStr, ctx.value);
  };

  const createRangeButton = () => {
    const buttonOptions = [
      {},
      {
        type: 'all',
        text: 'All',
        title: 'View all',
        events: {
          click() {
            setTimeout(() => {
              if (chartRef.current?.chart) {
                const { min, max } =
                  chartRef.current?.chart.xAxis[0].getExtremes();
                //根据数据区间 计算最大 最小值
                const range = (xMaxDate - xMinDate) / day;
                let newMax = xMaxDate;
                let newMin = xMinDate;
                //根据数据区间 前后追加时间 使图表更好看
                if (range < 30) {
                  //数据区间30天内 最大最小值前后追加1天
                  newMin = newMin - day;
                  newMax = newMax + day;
                } else if (range < 90) {
                  //数据区间90天内 最大最小值前后追加2天
                  newMax = newMax + day * 2;
                  newMin = newMin - day * 2;
                } else if (range < 180) {
                  //数据区间180天内 最大最小值前后追加5天
                  newMax = newMax + day * 5;
                  newMin = newMin - day * 5;
                } else {
                  //数据区间180天以上 最大最小值前后追加10天
                  newMax = newMax + day * 10;
                  newMin = newMin - day * 10;
                }
                if (min !== newMin || max !== newMax) {
                  chartRef.current.chart.fixedRange = 0;
                  chartRef.current.chart.xAxis[0].setExtremes(newMin, newMax);
                }
                delayUpdateXAxisOfDay(chartRef.current?.chart);
              }
            }, 0);
          },
        },
      },
      {
        type: 'day',
        count: 7,
        text: '1w',
        title: 'View 1 week',
        events: {
          click() {
            setTimeout(() => {
              if (chartRef.current?.chart) {
                const { min } = chartRef.current?.chart.xAxis[0].getExtremes();
                if (min !== xMinDate) {
                  chartRef.current.chart.fixedRange = 0;
                  chartRef.current.chart.xAxis[0].setExtremes(
                    xMinDate,
                    xMinDate + this._range,
                  );
                }
              }
              delayUpdateXAxisOfDay(chartRef.current?.chart);
            }, 0);
          },
        },
      },
      {
        type: 'month',
        count: 1,
        text: '1m',
        title: 'View 1 month',
        events: {
          click() {
            setTimeout(() => {
              if (chartRef.current?.chart) {
                const { min } = chartRef.current?.chart.xAxis[0].getExtremes();
                if (min !== xMinDate) {
                  chartRef.current.chart.fixedRange = 0;
                  chartRef.current.chart.xAxis[0].setExtremes(
                    xMinDate,
                    xMinDate + this._range,
                  );
                }
                delayUpdateXAxisOfDay(chartRef.current?.chart);
              }
            }, 0);
          },
        },
      },
      {
        type: 'month',
        count: 3,
        text: '3m',
        title: 'View 3 months',
        events: {
          click() {
            setTimeout(() => {
              if (chartRef.current?.chart) {
                const { min } = chartRef.current?.chart.xAxis[0].getExtremes();
                if (min !== xMinDate) {
                  chartRef.current.chart.fixedRange = 0;
                  chartRef.current.chart.xAxis[0].setExtremes(
                    xMinDate,
                    xMinDate + this._range,
                  );
                }
                delayUpdateXAxisOfDay(chartRef.current?.chart);
              }
            }, 0);
          },
        },
      },
      {
        type: 'month',
        count: 6,
        text: '6m',
        title: 'View 6 months',
        events: {
          click() {
            setTimeout(() => {
              if (chartRef.current?.chart) {
                const { min } = chartRef.current?.chart.xAxis[0].getExtremes();
                if (min !== xMinDate) {
                  chartRef.current.chart.fixedRange = 0;
                  chartRef.current.chart.xAxis[0].setExtremes(
                    xMinDate,
                    xMinDate + this._range,
                  );
                }
                delayUpdateXAxisOfDay(chartRef.current?.chart);
              }
            }, 0);
          },
        },
      },
    ];
    for (let i = 1; i <= nYear; i++) {
      buttonOptions.push({
        type: 'year',
        count: i,
        text: `${i}y`,
        title: `View ${i} year`,
        events: {
          click() {
            setTimeout(() => {
              if (chartRef.current?.chart) {
                const { min } = chartRef.current?.chart.xAxis[0].getExtremes();
                if (min !== xMinDate) {
                  chartRef.current.chart.fixedRange = 0;
                  chartRef.current.chart.xAxis[0].setExtremes(
                    xMinDate,
                    xMinDate + this._range,
                  );
                }
                delayUpdateXAxisOfDay(chartRef.current?.chart);
              }
            }, 0);
          },
        },
      });
    }
    return buttonOptions;
  };

  if (isLightMode) {
    GanttTooltip.style.color = '#000';
  } else {
    GanttTooltip.style.color = '#ffffff';
  }
  const result = {
    // title: {
    //     text: '123'
    // },
    tooltip: GanttTooltip,
    time: {
      useUTC: false,
    },
    plotOptions: {
      gantt: {
        borderColor: '',
      },
      series: {
        dataLabels: [
          {
            enabled: true,
            allowOverlap: true,
            style: {
              fontFamily: 'Helvetica Neue, Arial',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--fontColorHighContrast)',
            },
            formatter() {
              const { point = {} } = this;
              const { shapeArgs = {} } = point;

              if (point.isTick) {
                const functionBarTextandTick = `<div style="width: ${shapeArgs.width}px; height: 0.875rem; line-height: 1.75rem; position: relative;top:0px;">
                                          <div style="display:inline-block;position: relative; top:2px;left:-0.625rem; font-size:0.75rem; font-family: 'Helvetica Neue','Arial','sans-serif';">
                                            ${point.notes}
                                          </div>
                                        </div>`;
                return functionBarTextandTick;
              }
              return `<div style="width: ${shapeArgs.width}px; height: 0.875rem; line-height: 1.75rem; position: relative;top:-0.375rem;left:-0.625rem;" class="gantt-chart-label">
                                          <div style="display:inline-block;position: relative; top:2px; font-size:0.75rem; font-family: 'Helvetica Neue','Arial','sans-serif';">
                                            ${point.notes}
                                          </div>
                                        </div>`;
            },
            useHTML: true,
            align: 'left',
            x: 4,
          },
        ],
        pointPadding: 0.33,
      },
    },
    chart: {
      borderColor: '#474747',
      events: {
        render() {
          if (!isNotesHide) {
            this.series[0].points.forEach((point) => {
              if (point.graphic && point.notes) {
                point.graphic.translate(0, -6);
              }
            });
          }

          //  if all data (Item) on gantt chart is opened
          //  show "Collapse All" button
          const collapsedStateArray = this.series[0].data
            .filter((d) => d.id.startsWith('-L1-'))
            .map((x) => x.collapsed);

          const collapsedStateSet = new Set(collapsedStateArray);
          collapsedStateSet.delete(undefined);
          collapsedStateSet.size === 1 && collapsedStateSet.has(false)
            ? setIsCollapseAllButton(true)
            : setIsCollapseAllButton(false);

          // zoom text
          const rangeDiv = document.getElementsByClassName(
            'highcharts-range-selector-buttons',
          );
          if (rangeDiv) {
            for (const subele of rangeDiv) {
              const zoomTextTarget = subele.firstChild?.firstChild;
              if (zoomTextTarget) {
                zoomTextTarget.textContent = 'Date Range';
                if (!isLightMode) {
                  zoomTextTarget.style.fill = '#dbdbdb';
                }
              }
            }
          }
        },
        redraw() {
          // const currentChartRangeOfDays =
          //   (this.xAxis[0].getExtremes().userMax -
          //     this.xAxis[0].getExtremes().userMin) /
          //   day;
          // const smallTickElementsArray =
          //   document.getElementsByClassName('small-tick');
          // if (currentChartRangeOfDays > 500) {
          //   [...smallTickElementsArray].forEach((smallTick) => {
          //     smallTick.style.display = 'none';
          //   });
          // } else if (currentChartRangeOfDays > 366) {
          //   [...smallTickElementsArray].forEach((smallTick) => {
          //     smallTick.style.display = 'inline-block';
          //     smallTick.style.color = 'transparent';
          //   });
          // } else {
          //   [...smallTickElementsArray].forEach((smallTick) => {
          //     smallTick.style.display = 'inline-block';
          //     smallTick.style.color = 'white';
          //   });
          // }

          // for changing outerborder size when collapse or expand
          const visiableLineNumber = this.series[0].data.filter(
            (d) =>
              !d.id.startsWith('-L1-xAxisMinMax-') &&
              !d.id.startsWith('-L1-tick-') &&
              d.visible,
          ).length;

          //FYI: Dynamically change the height of left bolder when Collapse / Expand
          if (chartRef?.current?.container?.current) {
            const thisContainer = chartRef.current.container.current;
            const outerBolder =
              thisContainer.getElementsByClassName('outer-bolder');
            [...outerBolder].forEach((x) => {
              x.style.height = `${visiableLineNumber * RowHeight}px`;
            });
          }

          // if (currentChartRangeOfDays > 500) {
          //   [...smallTickElementsArray].forEach((smallTick) => {
          //     smallTick.style.display = 'none';
          //   });
          // }
          this.series[0].points.forEach((i) => {
            if (!i.isTick && i.notes && i.dataLabel.translateX < 0) {
              i.dataLabel.translate(0, i.dataLabel.translateY);
            }
          });
          setChartRedraw(true);
        },
      },
    },

    scrollbar: {
      enabled: true,
      height: 15,
      barBorderRadius: 10,
      trackBorderRadius: 10,
      margin: 15,
      // buttonsEnabled: true,
      barBackgroundColor: '#8D8D8C',
      trackBorderColor: 'transparent',
      buttonBackgroundColor: '#8D8D8C',
    },
    navigator: {
      enabled: true,
      liveRedraw: true,
      outlineColor: 'transparent',
      xAxis: {
        tickWidth: 0,
        lineWidth: 0,
        gridLineWidth: 1,
        tickPixelInterval: 200,
        labels: {
          align: 'left',
          style: {
            ...(isLightMode ? {} : { color: '#888' }),
            // color: '#888',
            opacity: 1,
          },
          x: 3,
          y: 12,
        },
      },
      series: {
        type: 'gantt',
        pointPlacement: 0.5,
        pointPadding: 0.25,
        accessibility: {
          enabled: false,
        },
      },
    },

    rangeSelector: {
      allButtonsEnabled: true,
      enabled: true,
      inputEnabled: false,
      buttonTheme: {
        fill: 'none',
        stroke: 'none',
        'stroke-width': 0,
        r: 8,
        style: {
          color: '#FFB600',
          fontWeight: 'bold',
        },
        states: {
          hover: {
            fill: '#e45c2b',
          },
          select: {
            fill: '#e45c2b',
            style: {
              color: isLightMode ? '#252525' : 'white',
            },
          },
        },
      },
      buttons: createRangeButton(),
      buttonPosition: {
        // x: -26,
        align: 'left',
      },
      inputStyle: {
        color: '#FFB600',
        fontWeight: 'bold',
      },
      selected: 0,
    },

    xAxis: [
      {
        minRange: day,
        // min: xAxisMin,
        // max: xAxisMax,
        // lineColor: '#474747',
        // tickColor: '#474747',
        // zIndex: 2,
        // gridLineWidth: 0,
        // gridLineColor: '#474747',
        className: 'gantt-xLabel',
        id: 'x-axis-for-day',
        grid: {
          cellHeight: 46,
        },
        // grid: { borderWidth: 0 },
        currentDateIndicator: {
          label: {
            style: { textOverflow: 'initial' },
            useHTML: true,
            formatter() {
              const _this = this;
              return `<div class="gantt-nowDate" style=font-family:Helvetica Neue Medium;>${moment(
                _this.value,
              ).format('l')}</div>`;
            },
          },
          color: '#E45C2B',
          zIndex: 4,
        },
        dateTimeLabelFormats: {
          // day: { list: ['%m/%e/%Y'] },
          day: { list: ['%m/%e/%Y'] },
          week: { list: ['%e %b'] },
          // month: { list: ['%b %Y', '%b'] },
          month: { list: ['%b'] },
        },
        // minorTickInterval: "auto",
        // minPadding: 5,
        // tickPixelInterval: 150,
        tickPositioner() {
          // manually calculate tick position to avoid empty data col in x-axis
          // console.log('cell length', this.len / this.tickPositions.length);
          const minTickInterval = getTickMinInterval(
            this.tickPositions.info?.unitName,
          );
          const yearXAxis = this.chart.get('x-axis-for-year');
          const tickNotAligned =
            typeof yearXAxis.tickPositions?.[0] === 'number' &&
            yearXAxis.tickPositions?.[0] !== this.tickPositions?.[0];
          if (
            this.tickPositions?.length &&
            yearXAxis.tickPositions?.length &&
            (this.len / this.tickPositions.length < minTickInterval ||
              tickNotAligned) &&
            this.tickPositions.info?.unitName !== 'year'
          ) {
            let tickInterval = getTickAdjustInterval(
              this.tickPositions.info?.unitName,
            );
            tickInterval = adjustTickIntervalByMaxYearAxisGap(
              yearXAxis,
              this,
              tickInterval,
            );

            const tickAmount = Math.floor(this.len / tickInterval);
            const duration = Math.ceil((this.max - this.min) / tickAmount);
            const result = [];

            // console.log('yearXAxis.unitName', yearXAxis?.tickPositions?.info?.unitName);

            if (
              yearXAxis &&
              yearXAxis.tickPositions?.length
              // && yearXAxis.tickPositions.info?.unitName === 'year'
            ) {
              // let minTickInterval = this.max;
              // for (let i = 0; i < yearXAxis.tickPositions.length - 1; i++) {
              //   minTickInterval = Math.min(yearXAxis.tickPositions[i + 1] - yearXAxis.tickPositions[i], minTickInterval);
              // }
              // const monthAmount = minTickInterval / (30 * 24 * 60 * 60 * 1000);
              for (let i = 0; i < yearXAxis.tickPositions?.length; i++) {
                result.push(yearXAxis.tickPositions[i]);
                while (
                  yearXAxis.tickPositions[i + 1] !== undefined &&
                  result[result.length - 1] + duration <
                    yearXAxis.tickPositions[i + 1]
                ) {
                  result.push(result[result.length - 1] + duration);
                }
              }
            } else {
              for (let i = 0; i <= tickAmount; i++) {
                const tick = this.min + i * duration;
                if (tick <= this.max) {
                  result.push(tick);
                }
              }
            }
            result.info = {
              unitRange: duration,
              count: 1,
              unitName: this.tickPositions.info.unitName,
              higherRanks: {},
              totalRange: duration,
            };
            return result;
          }
        },
        max: xMinAfterNYear > xMaxDate ? xMinAfterNYear : xMaxDate,
        labels: {
          formatter: ganttDateFormatter,
        },
        minTickInterval: day,
        events: {
          afterSetExtremes() {
            if (chartRef?.current?.chart) {
              setTimeout(() => {
                if (!chartRef?.current?.chart?.redraw) {
                  return;
                }
                chartRef.current.chart.redraw();
                updateXAxisOfDay(chartRef?.current?.chart);
              }, 650);
              // delayUpdateXAxisOfDay(chartRef?.current?.chart);
            }
          },
        },
      },
      {
        minRange: day,
        // min: xAxisMin,
        // max: xAxisMax,
        // lineColor: '#474747',
        // tickColor: '#474747',
        // zIndex: 2,
        // gridLineWidth: 0,
        // gridLineColor: '#474747',
        className: 'gantt-xLabel',
        id: 'x-axis-for-year',
        grid: {
          cellHeight: 46,
        },
        dateTimeLabelFormats: {
          // day: { list: ['%m/%e/%Y'] },
          day: { list: ['%m/%e/%Y'] },
          week: { list: ['%e %b'] },
          month: { list: ['%b %Y', '%b'] },
        },
        // minorTickInterval: "auto",
        // minPadding: 5,
        // tickPixelInterval: 50,
        // tickPositioner() {
        //   console.log(this.tickPositions);
        //
        // },
        // grid: { borderWidth: 0 },
        max: xMinAfterNYear > xMaxDate ? xMinAfterNYear : xMaxDate,
        labels: {
          formatter: ganttDateFormatter,
        },
        minTickInterval: day,
      },
    ],

    yAxis: {
      lineColor: '#474747',
      tickColor: '#474747',
      gridLineColor: '#474747',
      margin: 15,
      staticScale: RowHeight,
      tickWidth: 1,
      uniqueNames: true,
      grid: { borderWidth: 1 },
      title: {
        text: `<div>
        <div class="aaaaaa" style="height:2.125rem;text-align:left;padding-left:0.75rem;line-height:2.125rem;border:1px;"></div>
        <div class="bbbbbb" style="height:5.813rem;text-align:left;padding-left:0.75rem;background:var(--ganttYAxisBg);line-height:5.813rem;border:1px;border-color:var(--ganttBorderColor);border-style:solid;">Item</div>
        <div class="outer-bolder" style="height:${rightBorderLineHeight}px;text-align:left;padding-left:0.75rem;background:transprent;border:1px;border-left:0px;border-color:var(--ganttBorderColor);border-style:solid;"></div>
        </div>`,
        useHTML: true,
        textAlign: 'left',
        style: {
          ...(isLightMode ? {} : { color: '#dbdbdb' }),
          // color: '#dbdbdb',
          fontSize: '0.75rem',
          fontFamily: 'Helvetica Neue, Arial',
          // border: '1px solid #474747',
        },
      },
      labels: {
        style: {
          ...(isLightMode ? {} : { color: '#dbdbdb' }),
          // color: '#dbdbdb',
          fontSize: '0.875rem',
          lineHeight: '0.875rem',
          fontFamily: 'Helvetica Neue, Arial',
        },
        useHTML: true,
        formatter() {
          const _this = this;
          if (_this.value.length > maxL1LabelShowedLength) {
            return `<div title="${_this.value}">${_this.value.substring(
              0,
              maxL1LabelShowedLength,
            )}...</div>`;
          }
          return `<div title="${_this.value}" style="min-width:5rem">${_this.value}</div>`;
        },
      },
    },
    series,
    credits: {
      enabled: false,
    },
  };
  return result;
};
